import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "@angular/fire/database";
import { Observable, from, of } from "rxjs";
import {
  map,
  filter,
  switchMap,
  toArray,
  concatMap,
  take,
} from "rxjs/operators";
import { ColorCapaEnum } from "src/app/shared/enums/color-capa.enum";
import { StorageFirebaseService } from "./storage-firebase.service";
import { LoaderService } from "./loader.service";
import { LevantamientoObra } from "../models/entities/levantamiento-obra";
import { database } from "firebase";
import { GooglePolylinePoint } from "../models/google-polyline-point";
import { GooglePolyline } from "../models/google-polyline";

@Injectable({
  providedIn: "root",
})
export class AppMovilService {
  public path: string = "levantamientoObras";

  public constructor(
    public angularFireDatabase: AngularFireDatabase,
    public storageFirebase: StorageFirebaseService,
    public loaderService: LoaderService
  ) {}

  public guardarObra(obra: any) {
    obra.polylinePoints.forEach((point) => {
      this.angularFireDatabase
        .list(
          `${obra.tipoObra}/${obra.nombreEstado}/${obra.nombreObra}/${
            obra.nombreTramo
          }/fechas/${obra.fechaSinHora}/${obra.nombreTramo}/${
            obra.tipoObra === "app" ? "proyecto/tramo" : "ORI/tramoORI"
          }/${obra.fechaConHora}`
        )
        .push(point);
    });
  }

  public async guardarCapa(datosObra: any): Promise<any> {
    this.loaderService.activarLoaderScreen();
    let points = datosObra.polylinePoints;
    for (let index = 0; index < points.length; index++) {
      await this.angularFireDatabase
        .list(
          `${datosObra.tipoObra}/${datosObra.nombreEstado}/${datosObra.nombreObra}/${datosObra.nombreLicitacion}/fechas/${datosObra.fechaProyecto}/${datosObra.nombreLicitacion}/${datosObra.nombreCapa}/${datosObra.nombreTramo}/${datosObra.fechaProyectoTramo}`
        )
        .push(points[index]);
    }
    this.loaderService.desactivarLoaderScreen();
  }

  public async actualizarArchivoEnCoordenadaCapa(
    datosObra: any,
    lat: number,
    lng: number,
    rutaNuevoArchivo: string,
    tipoArchivo: "imagen" | "video"
  ): Promise<any> {
    this.loaderService.activarLoaderScreen();
    let path = `${datosObra.tipoObra}/${datosObra.nombreEstado}/${datosObra.nombreObra}/${datosObra.nombreLicitacion}/fechas/${datosObra.fechaProyecto}/${datosObra.nombreLicitacion}/${datosObra.nombreCapa}/${datosObra.nombreTramo}/${datosObra.fechaProyectoTramo}`;
    let coordenadas: any[] = await this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        take(1),
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      )
      .toPromise();
    let key = coordenadas.find((c) => c.lat === lat && c.lng === lng).key;
    if (tipoArchivo === "imagen") {
      await this.angularFireDatabase
        .list(path)
        .update(key, { ruta: rutaNuevoArchivo });
    } else {
      await this.angularFireDatabase
        .list(path)
        .update(key, { rutaVideo: rutaNuevoArchivo });
    }
    this.loaderService.desactivarLoaderScreen();
  }

  public guardarPuntoCoordenadaCapa(
    datosParaConstruirPath: any,
    point: any
  ): void {
    let datos = datosParaConstruirPath;
    this.angularFireDatabase
      .list(
        `${datos.tipoObra}/${datos.nombreEstado}/${datos.nombreObra}/${datos.nombreLicitacion}/fechas/${datos.fechaProyecto}/${datos.nombreLicitacion}/${datos.nombreCapa}/${datos.nombreTramo}/${datos.fechaProyectoTramo}`
      )
      .push(point);
  }

  public obtenerFechas(): Observable<string[]> {
    return this.angularFireDatabase
      .list<string>("app/estado/obra/licitacion/fechas")
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerProyectosPorFecha(fecha: string): Observable<string[]> {
    return this.angularFireDatabase
      .list<string>("app/estado/obra/licitacion/fechas/" + fecha)
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerFechaInicialProyecto(
    tipoObra: string,
    estado: string,
    obra: string,
    licitacionRecibida: string
  ): Observable<string> {
    return this.angularFireDatabase
      .list(`${tipoObra}/CR/${estado}/${obra}/${licitacionRecibida}/fechas`)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas: any) => fechas[0].key)
      );
  }

  public obtenerFechaInicialProyectoTramo(
    tipoObra: string,
    estado: string,
    obra: string,
    licitacionRecibida: string
  ): Observable<string> {
    return this.angularFireDatabase
      .list(`${tipoObra}/CR/${estado}/${obra}/${licitacionRecibida}/fechas`)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => fechas[0]),
        map((fecha) => {
          let licitacion = fecha[licitacionRecibida];
          let fechaTramo = "";
          if (
            licitacion.hasOwnProperty("proyecto") ||
            licitacion.hasOwnProperty("ORI")
          ) {
            let capa = licitacion.hasOwnProperty("proyecto")
              ? licitacion["proyecto"]
              : licitacion["ORI"];
            Object.keys(capa).forEach((keyTramo) => {
              let tramo = capa[keyTramo];
              // Object.keys(tramo).forEach(keyFechaTramo => {
              //   fechaTramo = tramo[keyFechaTramo];
              // })

              fechaTramo = Object.keys(tramo)[0];
            });
          }

          return fechaTramo;
        })
      );
  }

  public obtenerPolylineProyecto(
    tipoObra: string,
    estado: string,
    obra: string,
    licitacionRecibida: string
  ): Observable<GooglePolyline> {
    return this.angularFireDatabase
      .list(`${tipoObra}/CR/${estado}/${obra}/${licitacionRecibida}/fechas`)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => fechas[0]),
        map((fecha) => {
          let polyline: GooglePolyline = {
            color: ColorCapaEnum.PROYECTO,
            googlePolylinePoints: [],
          };
          let licitacion = fecha[licitacionRecibida];
          if (
            licitacion.hasOwnProperty("proyecto") ||
            licitacion.hasOwnProperty("ORI")
          ) {
            let capa = licitacion.hasOwnProperty("proyecto")
              ? licitacion["proyecto"]
              : licitacion["ORI"];
            Object.keys(capa).forEach((keyTramo) => {
              let tramo = capa[keyTramo];
              Object.keys(tramo).forEach((keyFechaTramo) => {
                let fechaTramo = tramo[keyFechaTramo];
                Object.keys(fechaTramo).forEach((id) => {
                  let googlePolylinePoint: GooglePolylinePoint = {
                    lat: fechaTramo[id].lat,
                    lng: fechaTramo[id].lng,
                  };
                  polyline.googlePolylinePoints.push(googlePolylinePoint);
                });
              });
            });
          }
          return polyline;
        })
      );
  }

  // public obtenerPolylinePorCapa(tipoObra: string, estado: string, obra: string, licitacionRecibida: string, capaRecibida: string) {
  //   return this.angularFireDatabase.list(`${tipoObra}/CR/${estado}/${obra}/${licitacionRecibida}/fechas`)
  //     .snapshotChanges()
  //     .pipe(
  //       map(changes => changes.map(c => ({ key: c.payload.key, ...c.payload.val() as any }))),
  //       switchMap(fechas => {
  //         return from(fechas).pipe(
  //           filter(fecha => fecha[licitacionRecibida].hasOwnProperty(capaRecibida)),
  //           map(fecha => {
  //             let polyline: GooglePolyline = {
  //               color: '',
  //               googlePolylinePoints: []
  //             };
  //             let licitacion = fecha[licitacionRecibida];
  //             if (licitacion.hasOwnProperty(capaRecibida)) {
  //               let capa = licitacion[capaRecibida];
  //               Object.keys(capa).forEach(keyTramo => {
  //                 let tramo = capa[keyTramo];
  //                 Object.keys(tramo).forEach(keyFechaTramo => {
  //                   let fechaTramo = tramo[keyFechaTramo];
  //                   Object.keys(fechaTramo).forEach(id => {
  //                     let googlePolylinePoint: GooglePolylinePoint = {
  //                       lat: fechaTramo[id].lat,
  //                       lng: fechaTramo[id].lng,
  //                     };
  //                     polyline.googlePolylinePoints.push(googlePolylinePoint);
  //                   })
  //                 })
  //               });
  //             }
  //             return polyline;
  //           }),
  //           map((polyline: GooglePolyline) => {
  //             let colorCapa: string = '';
  //             switch (capaRecibida) {
  //               case 'Avance': colorCapa = ColorCapaEnum.EN_PROCESO; break;
  //               case 'AvanceFull': colorCapa = ColorCapaEnum.TERMINADO; break;
  //               case 'Estructura': colorCapa = ColorCapaEnum.ESTRUCTURAS; break;
  //               case 'Problema': colorCapa = ColorCapaEnum.PROBLEMATICA; break;
  //               case 'proyecto': colorCapa = ColorCapaEnum.PROYECTO; break;
  //             }
  //             return { ...polyline, color: colorCapa }
  //           }),
  //           toArray()
  //         )
  //       })
  //     );
  // }

  public obtenerPolylinePorCapaPlaneacion(
    tipoObra: string,
    estado: string,
    obra: string,
    licitacionRecibida: string,
    fecha: string,
    capaRecibida: string
  ) {
    return this.angularFireDatabase
      .list(
        `${tipoObra}/CR/${estado}/${obra}/${licitacionRecibida}/fechas/${fecha}/${licitacionRecibida}/${capaRecibida}/tramo${capaRecibida}`
      )
      .snapshotChanges()
      .pipe(
        map((changes) => changes.map((c) => ({ ...(c.payload.val() as any) }))),
        switchMap((fechas) => {
          return from(fechas).pipe(
            map((fecha) => {
              let polyline = {
                color: "",
                points: [],
              };
              Object.keys(fecha).forEach((id) => {
                let point = fecha[id];
                polyline.points.push(point);
              });
              polyline.color =
                polyline.points.length > 0 ? polyline.points[0].colorPol : "";
              return polyline;
            }),
            toArray()
          );
        })
      );
  }

  public obtenerFechasConCoordenadas(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list(
        "appPruebas/CR/" +
          estado +
          "/" +
          obra +
          "/" +
          licitacion +
          "/fechas/" +
          fecha +
          "/" +
          licitacion +
          "/proyecto/tramo"
      )
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.val() as any[])));
  }

  public obtenerFechasConCoordenadasPlaneacion(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list(
        "appPlaneacionPruebas/CR/" +
          estado +
          "/" +
          obra +
          "/" +
          licitacion +
          "/fechas/" +
          fecha +
          "/" +
          licitacion +
          "/ORI/tramoORI"
      )
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.val() as any[])));
  }

  public obtenerFotosPorLicitacion(
    estado: string,
    obra: string,
    licitacionTmp: string
  ) {
    let path: string = `appPruebas/CR/${estado}/${obra}/${licitacionTmp}/fechas`;
    return this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => {
          let objetosFotos = [];
          fechas.forEach((fecha) => {
            let licitacion = fecha[licitacionTmp];
            let keysCapas = Object.keys(licitacion);
            keysCapas.forEach((keyCapa) => {
              let tramo = licitacion[keyCapa];
              let keysNombreTramo = Object.keys(tramo);
              keysNombreTramo.forEach((keyNombreTramo) => {
                let keysfechasTramos = Object.keys(tramo[keyNombreTramo]);
                keysfechasTramos.forEach((keyFechaTramo) => {
                  let objetoFecha = tramo[keyNombreTramo][keyFechaTramo];
                  let keysObjetosFecha = Object.keys(objetoFecha);
                  keysObjetosFecha.forEach((keyObjetoFecha) => {
                    if (objetoFecha[keyObjetoFecha].ruta) {
                      objetosFotos.push(
                        Object.assign(objetoFecha[keyObjetoFecha], {
                          nombreTramo: keyNombreTramo,
                          fechaTramo: keyFechaTramo,
                          nombreCapa: keyCapa,
                          nombreLicitacion: licitacionTmp,
                          fechaObra: fecha["key"],
                        })
                      );
                    }
                  });
                });
              });
            });
          });
          return objetosFotos;
        }),
        switchMap((objetosFotos) => {
          return from(objetosFotos).pipe(
            concatMap((objetoFoto) => {
              return this.storageFirebase
                .obtenerRutaArchivo(objetoFoto.ruta)
                .pipe(
                  filter((foto) => foto != null),
                  concatMap((foto) =>
                    of(Object.assign(objetoFoto, { ruta: foto }))
                  )
                );
            }),
            toArray(),
            map((objetoFotos) => objetoFotos.sort((a, b) => b.fecha - a.fecha)),
            map((objetoFotos) => objetoFotos.reverse())
          );
        })
      );
  }

  public obtenerRutasFotosFull(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ) {
    let path: string = `appPruebas/CR/${estado}/${obra}/${licitacion}/fechas/${fecha}/${licitacion}/AvanceFull/tramofull`;
    return this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => {
          let rutas = [];
          fechas.forEach((fecha) => {
            Object.keys(fecha).forEach((key) => {
              if (fecha[key].ruta) {
                rutas.unshift({
                  ruta: fecha[key].ruta,
                  descripcion: fecha[key].desc,
                });
              }
            });
          });
          let rutasDeUltimasCuatroFotos = [];
          if (rutas.length > 0) {
            let rutasAgregadas: number = 0;
            for (let indice = 0; indice < rutas.length; indice++) {
              if (rutasAgregadas < 4) {
                rutasDeUltimasCuatroFotos.unshift({
                  ruta: rutas[indice].ruta,
                  descripcion: rutas[indice].descripcion,
                });
                rutasAgregadas++;
              } else {
                break;
              }
            }
          }
          return rutasDeUltimasCuatroFotos;
        }),
        switchMap((rutas) => {
          return from(rutas).pipe(
            concatMap((ruta) =>
              of({
                descripcion: ruta.descripcion,
                ruta: this.storageFirebase.obtenerRutaArchivo(ruta.ruta),
              })
            ),
            toArray()
          );
        })
      );
  }

  public obtenerRutasFotosAvance(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ) {
    let path: string = `appPruebas/CR/${estado}/${obra}/${licitacion}/fechas/${fecha}/${licitacion}/Avance/tramoavance`;
    return this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => {
          let rutas = [];
          fechas.forEach((fecha) => {
            Object.keys(fecha).forEach((key) => {
              if (fecha[key].ruta) {
                rutas.unshift({
                  ruta: fecha[key].ruta,
                  descripcion: fecha[key].desc,
                });
              }
            });
          });
          let rutasDeUltimasCuatroFotos = [];
          if (rutas.length > 0) {
            let rutasAgregadas: number = 0;
            for (let indice = 0; indice < rutas.length; indice++) {
              if (rutasAgregadas < 4) {
                rutasDeUltimasCuatroFotos.unshift({
                  ruta: rutas[indice].ruta,
                  descripcion: rutas[indice].descripcion,
                });
                rutasAgregadas++;
              } else {
                break;
              }
            }
          }
          return rutasDeUltimasCuatroFotos;
        }),
        switchMap((rutas) => {
          return from(rutas).pipe(
            concatMap((ruta) =>
              of({
                descripcion: ruta.descripcion,
                ruta: this.storageFirebase.obtenerRutaArchivo(ruta.ruta),
              })
            ),
            toArray()
          );
        })
      );
  }

  public obtenerRutasFotosEstructura(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ) {
    let path: string = `appPruebas/CR/${estado}/${obra}/${licitacion}/fechas/${fecha}/${licitacion}/Estructura/tramoestructura`;
    return this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => {
          let rutas = [];
          fechas.forEach((fecha) => {
            Object.keys(fecha).forEach((key) => {
              if (fecha[key].ruta) {
                rutas.unshift({
                  ruta: fecha[key].ruta,
                  descripcion: fecha[key].desc,
                });
              }
            });
          });
          let rutasDeUltimasCuatroFotos = [];
          if (rutas.length > 0) {
            let rutasAgregadas: number = 0;
            for (let indice = 0; indice < rutas.length; indice++) {
              if (rutasAgregadas < 4) {
                rutasDeUltimasCuatroFotos.unshift({
                  ruta: rutas[indice].ruta,
                  descripcion: rutas[indice].descripcion,
                });
                rutasAgregadas++;
              } else {
                break;
              }
            }
          }
          return rutasDeUltimasCuatroFotos;
        }),
        switchMap((rutas) => {
          return from(rutas).pipe(
            concatMap((ruta) =>
              of({
                descripcion: ruta.descripcion,
                ruta: this.storageFirebase.obtenerRutaArchivo(ruta.ruta),
              })
            ),
            toArray()
          );
        })
      );
  }

  public obtenerRutasFotosProblematica(
    estado: string,
    obra: string,
    licitacion: string,
    fecha: string
  ) {
    let path: string = `appPruebas/CR/${estado}/${obra}/${licitacion}/fechas/${fecha}/${licitacion}/Problema/tramoproblema`;
    return this.angularFireDatabase
      .list(path)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((fechas) => {
          let rutas = [];
          fechas.forEach((fecha) => {
            Object.keys(fecha).forEach((key) => {
              if (fecha[key].ruta) {
                rutas.unshift({
                  ruta: fecha[key].ruta,
                  descripcion: fecha[key].desc,
                });
              }
            });
          });
          let rutasDeUltimasCuatroFotos = [];
          if (rutas.length > 0) {
            let rutasAgregadas: number = 0;
            for (let indice = 0; indice < rutas.length; indice++) {
              if (rutasAgregadas < 4) {
                rutasDeUltimasCuatroFotos.unshift({
                  ruta: rutas[indice].ruta,
                  descripcion: rutas[indice].descripcion,
                });
                rutasAgregadas++;
              } else {
                break;
              }
            }
          }
          return rutasDeUltimasCuatroFotos;
        }),
        switchMap((rutas) => {
          return from(rutas).pipe(
            concatMap((ruta) =>
              of({
                descripcion: ruta.descripcion,
                ruta: this.storageFirebase.obtenerRutaArchivo(ruta.ruta),
              })
            ),
            toArray()
          );
        })
      );
  }

  public obtenerEstadosApp(): Observable<string[]> {
    return this.angularFireDatabase
      .list<any>("appPruebas/CR")
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerEstadosObrasApp(): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPruebas/CR")
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((d) =>
          d.map((o) => ({
            estado: o.key,
            obras: Object.keys(o).filter((f) => f !== "key"),
          }))
        )
      );
  }

  public obtenerEstadosObrasAppPlaneacion(): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPlaneacionPruebas/CR")
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.key,
            ...(c.payload.val() as any),
          }))
        ),
        map((d) =>
          d.map((o) => ({
            estado: o.key,
            obras: Object.keys(o).filter((f) => f !== "key"),
          }))
        )
      );
  }

  public obtenerEstadosByTipoObra(tipoObra: string): Observable<string[]> {
    return this.angularFireDatabase
      .list<any>(`${tipoObra}/CR`)
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerObrasByEstadoAndTipoObra(
    estado: string,
    tipoObra: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>(`${tipoObra}/CR/${estado}`)
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerLicitacionesByObraAndTipoObra(
    tipoObra: string,
    estado: string,
    obra: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>(`${tipoObra}/CR/${estado}/${obra}`)
      .snapshotChanges()
      .pipe(map((changes) => changes.map((c) => c.payload.key)));
  }

  public obtenerObrasTemporalesByEstado(keyEstado: string): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPruebas/CR/" + keyEstado)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  /** Metodo utilizado para obtener las obras segun el estado seleccionado */
  public findObrasByKeyEstado(
    keyCatEstado: string
  ): Observable<LevantamientoObra[]> {
    return this.angularFireDatabase
      .list<LevantamientoObra>(this.path, (ref: database.Reference) =>
        ref.orderByChild("keyCatEstado").equalTo(keyCatEstado)
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map(
            (c) =>
              ({
                key: c.payload.key,
                ...c.payload.val(),
              } as LevantamientoObra)
          )
        )
      );
  }

  public obtenerObrasPlaneacionByEstado(keyEstado: string): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPlaneacionPruebas/CR/" + keyEstado)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerLicitacionesTemporalesByObra(
    keyEstado: string,
    keyObra: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPruebas/CR/" + keyEstado + "/" + keyObra)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerLicitacionesPlaneacionByObra(
    keyEstado: string,
    keyObra: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPlaneacionPruebas/CR/" + keyEstado + "/" + keyObra)
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerFechasTemporalesByLicitacion(
    keyEstado: string,
    keyObra: string,
    keyLicitacion: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>(
        "appPruebas/CR/" +
          keyEstado +
          "/" +
          keyObra +
          "/" +
          keyLicitacion +
          "/fechas"
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerFechasPlaneacionByLicitacion(
    keyEstado: string,
    keyObra: string,
    keyLicitacion: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>(
        "appPlaneacionPruebas/CR/" +
          keyEstado +
          "/" +
          keyObra +
          "/" +
          keyLicitacion +
          "/fechas"
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerCapasTemporalesByFecha(
    keyEstado: string,
    keyObra: string,
    keyLicitacion: string,
    keyFecha: string
  ): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>(
        "appPruebas/CR/" +
          keyEstado +
          "/" +
          keyObra +
          "/" +
          keyLicitacion +
          "/fechas/" +
          keyFecha +
          "/" +
          keyLicitacion
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => {
            if (c.payload.key === "Avance") {
              return { key: c.payload.key, val: "Avance" };
            }
            if (c.payload.key === "AvanceFull") {
              return { key: c.payload.key, val: "Avance completo" };
            }
            if (c.payload.key === "Estructura") {
              return { key: c.payload.key, val: "Estructura" };
            }
            if (c.payload.key === "Problema") {
              return { key: c.payload.key, val: "Problemática" };
            }
            if (c.payload.key === "proyecto") {
              return { key: c.payload.key, val: "Proyecto" };
            }
          })
        )
      );
  }

  public obtenerDistanciaPorCapaFecha(
    keyEstado: string,
    keyObra: string,
    keyLicitacion: string,
    keyFecha: string,
    keyCapa: string
  ) {
    return this.angularFireDatabase
      .list<any>(
        `appPruebas/CR/${keyEstado}/${keyObra}/${keyLicitacion}/fechas/${keyFecha}/${keyLicitacion}/${keyCapa}`
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            ...(c.payload.val() as any),
          }))
        ),
        map((tramos) => {
          let tramo = tramos[0];
          let primerFecha = Object.keys(tramo)[0];
          let ultimaFecha = Object.keys(tramo)[Object.keys(tramo).length - 1];
          let coordenadaPrimerFecha = Object.values(tramo[primerFecha])[0];
          let coordenadaUltimaFecha = Object.values(tramo[ultimaFecha])[
            Object.values(tramo[ultimaFecha]).length - 1
          ];
          return {
            primerCoordenada: coordenadaPrimerFecha,
            coordenadaUltimaFecha: coordenadaUltimaFecha,
          } as any;
        })
      );
  }

  public obtenerCatalogoEstados(): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("catalogos/catEstados/catalogo")
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }

  public obtenerEstadosPlaneacion(): Observable<any[]> {
    return this.angularFireDatabase
      .list<any>("appPlaneacionPruebas/CR")
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            key: c.payload.key,
            ...(c.payload.val() as any),
          }))
        )
      );
  }
}
